import PropTypes from "prop-types";
import React, { useRef } from "react";
import Info from "./Info";
import Notification from "./Notification";
import QuotaLimit from "./quota/QuotaLimit";
import useUserTimeZoneDataStorage from "../utils/useUserTimeZoneDataStorage";
import CollectionSelect from "./CollectionSelect";
import {
  ActionIcon,
  Box,
  CopyButton,
  Flex,
  Image,
  Text,
  TextInput,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import CustomTooltip from "./mantineCustomComponents/CustomTooltip";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import { useStylesGeneral } from "../utils/mantine/stampTabContent/useStylesGeneral";
import CustomButton from "./mantineCustomComponents/CustomButton";
import { useStylesLanding } from "../utils/mantine/useStylesLanding";
import { useStylesButton } from "../utils/mantine/useStylesButton";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useStylesIcons } from "../utils/mantine/useStylesIcons";
import ValidationError from "./ValidationError";
import {
  HASH_ALGORITHM_SHA3_256,
  HASH_ALGORITHM_KECCAK_256,
} from "../utils/cryptoUtils";

const StampFromFile = ({
  currentScreen,
  staticBasePath,
  objectHash,
  selectedFileName,
  handleFileInput,
  isAuthenticated,
  isDisabled,
  helpIsActive,
  noQuotaAvailable,
  onSelectedOptionCollection,
  selectedOptionCollection,
  activateSelectCollection,
  onActivateSelectCollection,
  selectCollections,
  filePortfolioValidation,
}) => {
  // css classes
  const { classes: tooltipClasses } = useStylesTooltip();

  const { classes: GeneralClasses } = useStylesGeneral();

  const { classes: buttonClasses } = useStylesButton();

  const { classes: landingClasses } = useStylesLanding();

  const { classes: classesIcons } = useStylesIcons();

  const fileInputRef = useRef(null);
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Data storage s3 or not
  const { dataStorage } = useUserTimeZoneDataStorage();

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  return (
    <Box id="verify-method-file__screen" className={GeneralClasses.wrap}>
      <Box className={GeneralClasses.wrapContent}>
        {currentScreen === "found" && (
          <Info
            textInfo="You have already stamped this file previously. You can choose another file or stamp this file again."
            staticBasePath={staticBasePath}
          />
        )}

        <Flex
          direction="column"
          justify={theme.flex.justify.start}
          align={theme.flex.align.center}
          id="drop-area"
          onDragOver={(e) => e.preventDefault()}
          onDrop={!noQuotaAvailable && handleFileInput}
          classNames={{
            root: `${GeneralClasses.wrapContentContainer} ${
              GeneralClasses.wrapContentContainerRelative
            }
              ${
                isAuthenticated
                  ? ""
                  : GeneralClasses.wrapContentContainerDisabled
              } ${
              !noQuotaAvailable
                ? ""
                : GeneralClasses.wrapContentContainerDisabled
            } ${
              objectHash && currentScreen !== "found"
                ? ""
                : GeneralClasses.wrapContentContainerNoHash
            }
              `,
          }}
        >
          <Box className={GeneralClasses.wrapContentContainerPadding}>
            {!isAuthenticated && <Notification />}
            {noQuotaAvailable && <QuotaLimit />}

            {helpIsActive && (
              <CustomTooltip
                classNames="tooltip-top"
                content={
                  <Text
                    classNames={{
                      root: tooltipClasses.tooltipText,
                    }}
                  >
                    Selecting a file calculates its digital fingerprint (aka:
                    ContentID or hash). Uses {HASH_ALGORITHM_SHA3_256} for new
                    stamps; {HASH_ALGORITHM_KECCAK_256} supported for
                    verification. This calculation is done inside your browser,
                    the file is never transmitted anywhere outside your
                    computer.
                  </Text>
                }
              ></CustomTooltip>
            )}
            {objectHash && currentScreen !== "found" && (
              <Flex
                align={theme.flex.align.center}
                className={GeneralClasses.stampFileLoaded}
              >
                <Image
                  src={`${staticBasePath}public/images/svg/rb_file_uploaded.svg`}
                  alt="Uploaded"
                />
                <Text component="span">File Ready.</Text>
              </Flex>
            )}

            <ValidationError validation={filePortfolioValidation} />

            <CustomButton
              text={
                isDisabled ? "Browse for file" : "Browse for a different file"
              }
              id="fileOpenTrigger"
              icon={isDisabled ? "IconVerifyFromFile" : undefined}
              onClick={() => {
                if (isAuthenticated && !noQuotaAvailable) {
                  handleClick();
                }
              }}
              componentClasses={`btn-stamp-upload ${
                buttonClasses.buttonBlack
              } ${landingClasses.pricingBlockButtonsButton} ${
                GeneralClasses.buttonFile
              }

            ${
              isAuthenticated
                ? ""
                : GeneralClasses.buttonFileDisabled + " btn-stamp-disabled"
            }  ${
                !noQuotaAvailable
                  ? ""
                  : GeneralClasses.buttonFileDisabled + " btn-stamp-disabled"
              } ${
                objectHash && currentScreen !== "found"
                  ? GeneralClasses.buttonFileHash
                  : ""
              }
 ${isDisabled ? "" : GeneralClasses.buttonTextFilled}
            `}
            />
            <Box component="div" className={GeneralClasses.fileDropzoneText}>
              {isDisabled
                ? "Or just drag and drop file here"
                : "or drag and drop here"}
            </Box>
          </Box>

          {objectHash && (
            <Flex className={GeneralClasses.stampHash}>
              {selectedFileName && (
                <Box
                  className="stamp-hash-text stamp-hash-filename"
                  component="div"
                >
                  File: {selectedFileName}
                </Box>
              )}
              <Flex className="stamp-hash-row">
                <Box className="stamp-hash-text"> File Content ID:</Box>
                <Box className="stamp-hash-content">
                  {objectHash}

                  <CopyButton value={objectHash} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? "Copied" : "Copy"}
                        className={classesIcons.copy}
                        withArrow
                        position="right"
                      >
                        <ActionIcon
                          color={
                            copied
                              ? theme.customColors.actionIconFirst
                              : theme.customColors.actionIconSecond
                          }
                          variant="subtle"
                          onClick={copy}
                        >
                          {copied ? (
                            <IconCheck className={classesIcons.width} />
                          ) : (
                            <IconCopy className={classesIcons.width} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Box>
              </Flex>
            </Flex>
          )}
        </Flex>

        {selectCollections && isAuthenticated && (
          <CollectionSelect
            activateSelectCollection={activateSelectCollection}
            onActivateSelectCollection={onActivateSelectCollection}
            selectCollections={selectCollections}
            onSelectedOptionCollection={onSelectedOptionCollection}
            selectedOptionCollection={selectedOptionCollection}
            helpIsActive={helpIsActive}
          />
        )}

        <Flex
          justify={theme.flex.justify.center}
          align={theme.flex.align.center}
          className={GeneralClasses.stampInfo}
        >
          <Image
            src={`${staticBasePath}public/images/svg/private_local.svg`}
            alt="Info"
          />
          {dataStorage === "s3" ? (
            <Text>
              Stamped data will be saved in your account’s private folder. To
              disable data storage,{" "}
              <CustomAnchor
                text="update your account settings."
                href="/profile/#account_settings"
                componentClasses="link"
              />
            </Text>
          ) : (
            <Box pr={theme.padding.smEm}>
              <Text>
                The file is processed locally on your computer. It is never sent
                to vBase or anywhere else.
              </Text>
              <Text>
                To enable automated backup of Stamped Files,{" "}
                <CustomAnchor
                  text="update your account settings."
                  href="/profile/#account_settings"
                  componentClasses="link"
                />
              </Text>
            </Box>
          )}

          {helpIsActive && dataStorage !== "s3" && (
            <CustomTooltip
              content={
                <>
                  <Text
                    classNames={{
                      root: tooltipClasses.tooltipText,
                    }}
                  >
                    It’s your data, and privacy is important. The vBase Stamper
                    works without transmitting your data.
                  </Text>
                </>
              }
            ></CustomTooltip>
          )}
        </Flex>
        <TextInput
          type="file"
          ref={fileInputRef}
          id="fileInput"
          style={{ display: "none" }}
          onChange={handleFileInput}
        />
      </Box>
    </Box>
  );
};

StampFromFile.propTypes = {
  currentScreen: PropTypes.string.isRequired,
  staticBasePath: PropTypes.string.isRequired,
  objectHash: PropTypes.string,
  selectedFileName: PropTypes.string,
  handleFileInput: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  helpIsActive: PropTypes.bool.isRequired,
  noQuotaAvailable: PropTypes.bool.isRequired,
  onSelectedOptionCollection: PropTypes.func.isRequired,
  selectedOptionCollection: PropTypes.string,
  activateSelectCollection: PropTypes.bool.isRequired,
  onActivateSelectCollection: PropTypes.func,
  selectCollections: PropTypes.array,
  filePortfolioValidation: PropTypes.object,
};
export default StampFromFile;
